/**
 * LocationPicker — single picker component used everywhere a location
 * is attached to an entity (event venue, session, travel
 * touchpoint, standalone Location form).
 *
 * Core concepts:
 *
 *   1. `verified` provenance flag. A Location is either Verified (data
 *      came from a MapKit Place pick and hasn't been hand-edited since)
 *      or Custom (manually authored, or drifted from a prior pick). The
 *      chip at the top of the picker surfaces this. Verification is
 *      metadata about provenance — Custom locations are first-class
 *      citizens; they save, render pins on the map, and work in every
 *      consumer.
 *
 *   2. `locationId` link. When the picker is pointing at an existing
 *      saved Location (either loaded from a parent entity's reference,
 *      or selected from the "Existing locations" group of the
 *      autocomplete dropdown), `value.locationId` holds that doc's id.
 *      A persistent "Linked location — edits apply everywhere" banner
 *      appears so the organizer is aware that field edits will mutate
 *      the shared doc. An Unlink button detaches the link and forks the
 *      current data into a new Location at submit time.
 *
 *   3. Flat value shape (no discriminated union). Both "linked to
 *      existing" and "authoring new" use the same shape, distinguished
 *      only by whether `locationId` is set. Edits flow through one set
 *      of handlers regardless of whether they end up updating an
 *      existing doc or creating a new one. resolveLocationPickerValue
 *      handles the update-vs-create branch at submit time.
 *
 *   4. Bidirectional sync. Typing City/State/Country biases the venue
 *      autocomplete (geocoded internally, cached). Picking a venue from
 *      autocomplete cascade-fills the address fields. The two surfaces
 *      stay coherent.
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Grid,
  TextField,
  Autocomplete as MuiAutocomplete,
  Typography,
  Paper,
  IconButton,
  ToggleButton,
  Tooltip,
  CircularProgress,
  Button,
} from '@mui/material';
import { MuiColorInput } from 'mui-color-input';
import { Check, ChevronDown, MapPin, Pencil, Plus, X } from 'lucide-react';
import { Map, Marker, type MapInteractionEvent } from 'mapkit-react';
import { ensureMapKitJsLoaded } from '../../lib/mapkit';
import { isWebGLAvailable } from '../../utils/webgl';
import { MapErrorBoundary } from './MapErrorBoundary';
import { useEventLocations } from '../../hooks/useEventLocations';
import {
  MapKitAutocompleteField,
  type ResolvedPlaceParts,
} from './MapKitAutocompleteField';
import Chip from '@mui/material/Chip';
import { useIsPhone } from '../../hooks/useIsPhone';
import type { EventLocation } from '@event-planner/shared';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

/**
 * Single flat shape representing the picker's current state.
 *
 * Two ids together drive the save behavior + the Editing/New toggle:
 *
 *   - `locationId`: the doc to write to at submit time. null means
 *     "create a new doc"; non-null means "update this doc".
 *
 *   - `originalLocationId`: captured once at hydration (parent form
 *     loaded with a saved id) or when the user picks from "Existing
 *     locations" in the autocomplete. Persists across edits even when
 *     the user toggles to "New copy" mode (which sets `locationId` to
 *     null but leaves `originalLocationId` intact, so the toggle can
 *     swing back).
 *
 *   - When `locationId === originalLocationId`: button shows "Editing"
 *     and save will update the original.
 *   - When `locationId === null` but `originalLocationId !== null`:
 *     button shows "New copy" and save will create a new doc, leaving
 *     the original untouched.
 *   - When both are null: pure new-authoring session. The Editing/New
 *     button is hidden — there's nothing to fork from.
 */
export type LocationPickerValue = {
  locationId: string | null;
  originalLocationId: string | null;
  verified: boolean;
  label: string;
  coordinates: { lat: number; lng: number } | null;
  address: string | null;
  city: string | null;
  stateOrRegion: string | null;
  country: string | null;
  color: string;
} | null;

export interface LocationPickerProps {
  eventId: string;
  value: LocationPickerValue;
  onChange: (next: LocationPickerValue) => void;
  /**
   * Default color for new locations. Smart default per parent form
   * context (category color from SessionForm, etc.). Falls back to
   * neutral gray when omitted.
   */
  defaultColor?: string;
  /** Test ID suffix per call site. */
  testIdSuffix?: string;
  /** Map preview height in pixels when the toggle is on. */
  mapHeight?: number;
  /**
   * Optional geographic bias coordinate from the parent (e.g. the
   * event's venue coords used by Session/Group/Travel pickers so their
   * searches stay local to the event). Falls back behind the picker's
   * own typed-address-derived bias.
   */
  searchBiasCoordinate?: { lat: number; lng: number };
  /**
   * Visual layout of the map + form below the header row.
   *
   *   - `split` (default): map on the left (md=7), address fields +
   *     pin color on the right (md=5). The standard design used
   *     everywhere the picker appears (LocationForm, EventForm,
   *     SessionForm, GroupForm, TravelForm). Stacks vertically below
   *     the md breakpoint.
   *   - `stacked`: map full-width above, address fields and pin color
   *     stacked below. Legacy single-column layout, retained as an
   *     opt-out for narrow embed contexts that don't have room for
   *     the side-by-side design.
   */
  layout?: 'stacked' | 'split';
}

// ---------------------------------------------------------------------------
// Internal autocomplete option type
// ---------------------------------------------------------------------------

type GroupedOption =
  | {
      kind: 'existing';
      id: string;
      label: string;
      sublabel: string | null;
      raw: EventLocation;
    }
  | {
      kind: 'mapkit';
      label: string;
      sublabel: string;
      raw: mapkit.SearchAutocompleteResult;
    };

const DEFAULT_NEUTRAL_COLOR = '#607D8B';

/**
 * In-memory cache of geocoded bias text → coordinate. Process-lifetime;
 * never invalidated. Plain object instead of `new Map(...)` because
 * `Map` is shadowed by the mapkit-react Map component import.
 */
const geocodeCache: Record<string, { lat: number; lng: number } | null> = {};

function normalizeBiasText(text: string): string {
  return text.trim().toLowerCase().replace(/\s+/g, ' ');
}

/**
 * Helper for parent forms — convert a saved EventLocation into a fresh
 * picker value. Used at form-init time when the parent already has the
 * full location (rare in admin where lookups are async).
 */
export function pickerValueFromLocation(
  loc: EventLocation,
): LocationPickerValue {
  return {
    locationId: loc.id,
    originalLocationId: loc.id,
    verified: loc.verified,
    label: loc.label,
    coordinates: loc.coordinates,
    address: loc.address,
    city: loc.city,
    stateOrRegion: loc.stateOrRegion,
    country: loc.country,
    color: loc.color,
  };
}

/**
 * Helper for parent forms — initialize the picker with a locationId
 * stub when only the id is available (the typical case: parent has
 * `event.venueLocationId` but no live EventLocation yet). The picker's
 * internal hydration effect fills the rest from its existingLocations
 * subscription on first render.
 *
 * Parents that may save without ever mounting the picker (e.g. tabbed
 * forms where the picker lives behind a non-active tab) MUST also
 * track a baseline ref and gate `confirmAndResolve` on
 * `isPickerValueDirty(baseline, current)`. Without that gate, the save
 * flow always runs the resolver against the picker value, which writes
 * to the linked location doc on every save. See the FORM-LEVEL DIRTY
 * GATE block in EventForm / SessionForm / TravelForm for the canonical
 * pattern.
 */
export function pickerValueStub(
  locationId: string,
  defaultColor = DEFAULT_NEUTRAL_COLOR,
): LocationPickerValue {
  return {
    locationId,
    originalLocationId: locationId,
    verified: false,
    label: '',
    coordinates: null,
    address: null,
    city: null,
    stateOrRegion: null,
    country: null,
    color: defaultColor,
  };
}

/**
 * Compare a picker value against its baseline (the value at form-init
 * time, optionally bumped to the post-hydration value once the stub
 * fills in). Returns true if the user has materially edited the
 * picker since baseline.
 *
 * "Material" = any user-editable field differs: locationId,
 * originalLocationId, verified, label, coordinates (deep), address,
 * city, stateOrRegion, country, color.
 *
 * Special case: same locationId, current is an unhydrated stub
 * (label empty, coords null, all address parts null). Treated as NOT
 * dirty — the picker simply never mounted to hydrate, so the value
 * looks empty even though the underlying doc is unchanged. This is
 * the load-bearing case the form-level dirty gate uses to short-
 * circuit "open form, jump to sibling tab, save" without touching the
 * linked location doc.
 *
 * Used by EventForm, SessionForm, TravelForm to gate
 * `confirmAndResolve`. LocationForm does NOT use this — its picker
 * IS the form, and RHF's `formState.isDirty` already covers it.
 */
export function isPickerValueDirty(
  baseline: LocationPickerValue,
  current: LocationPickerValue,
): boolean {
  if (baseline === current) return false;
  if (!baseline && !current) return false;
  if (!baseline || !current) return true;

  // Different locationId → user picked a different venue.
  if (baseline.locationId !== current.locationId) return true;
  if (baseline.originalLocationId !== current.originalLocationId) return true;

  // Unhydrated-stub short-circuit. If current is a stub and baseline
  // is also pointing at the same location, the picker simply never
  // mounted to fill in the details — no real edit happened.
  const currentIsStub =
    !current.label.trim() &&
    !current.coordinates &&
    !current.address &&
    !current.city &&
    !current.stateOrRegion &&
    !current.country;
  if (currentIsStub && baseline.locationId === current.locationId) return false;

  if (baseline.verified !== current.verified) return true;
  if (baseline.label !== current.label) return true;
  if (baseline.address !== current.address) return true;
  if (baseline.city !== current.city) return true;
  if (baseline.stateOrRegion !== current.stateOrRegion) return true;
  if (baseline.country !== current.country) return true;
  if (baseline.color !== current.color) return true;

  // Coordinates are an object — compare components.
  const a = baseline.coordinates;
  const b = current.coordinates;
  if (!a && !b) return false;
  if (!a || !b) return true;
  if (a.lat !== b.lat || a.lng !== b.lng) return true;

  return false;
}

// ---------------------------------------------------------------------------
// Map container size gate
// ---------------------------------------------------------------------------

/**
 * True once the observed element has a non-zero box; false while it is
 * collapsed (`display: none` tab, background tab before first layout,
 * mid-resize). MapKit computes its initial zoom from the box and throws
 * NaN ("MapRect.scale() ... is not a number", ADR-128) when the box is
 * degenerate at construction, so <Map> must not mount until this is true.
 *
 * ResizeObserver delivers the current size on observe() and again on
 * every change, so a box that collapses later flips this back to false
 * and unmounts (destroys) the map; it remounts when the box returns.
 * Environments without ResizeObserver (jsdom) are treated as sized so
 * the picker keeps its existing behaviour there.
 */
function useNonZeroBox(): [(node: HTMLElement | null) => void, boolean] {
  const [sized, setSized] = useState(typeof ResizeObserver === 'undefined');
  const observerRef = useRef<ResizeObserver | null>(null);
  const ref = useCallback((node: HTMLElement | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;
    if (!node || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver((entries) => {
      const rect = entries[entries.length - 1]?.contentRect;
      setSized(Boolean(rect && rect.width > 0 && rect.height > 0));
    });
    observer.observe(node);
    observerRef.current = observer;
  }, []);
  return [ref, sized];
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function LocationPicker({
  eventId,
  value,
  onChange,
  defaultColor,
  testIdSuffix,
  mapHeight = 320,
  searchBiasCoordinate,
  layout: layoutProp = 'split',
}: LocationPickerProps) {
  // Phones always use the stacked layout: the split layout's map column
  // stretches to 100% height and pushes the coordinates over the address
  // fields once the two columns stack.
  const isPhone = useIsPhone();
  const layout = isPhone ? 'stacked' : layoutProp;
  // Phone only: whether the map takes touch input (see the map cover below).
  const [mapUnlocked, setMapUnlocked] = useState(false);
  const testId = (base: string) =>
    testIdSuffix ? `${base}-${testIdSuffix}` : base;

  const { locations: existingLocations } = useEventLocations(eventId);

  const initialColor = defaultColor ?? DEFAULT_NEUTRAL_COLOR;

  // Hydration: when value carries a locationId but blank fields (e.g.
  // parent form initialized from a saved venueLocationId before the
  // locations subscription resolved), fill in from the linked doc.
  useEffect(() => {
    if (!value?.locationId || value.label) return;
    const linked = existingLocations.find((l) => l.id === value.locationId);
    if (!linked) return;
    onChange(pickerValueFromLocation(linked));
  }, [value, existingLocations, onChange]);

  // mapPinOn carries two meanings that the original implementation
  // conflated: (1) the user wants to be in autocomplete-search mode,
  // (2) we have coordinates to display. The useEffect below syncs
  // (2) into (1) — useful when the parent hydrates an existing
  // location stub asynchronously (`pickerValueStub` arrives without
  // coords, `pickerValueFromLocation` follows with coords). But the
  // dep MUST be a stable boolean: using `value?.coordinates` directly
  // re-fires on undefined → null transitions (both are falsy), which
  // happens whenever the user toggles pin on then types a single
  // character and `value` materializes from null with `coordinates:
  // null`. That false re-fire silently resets mapPinOn to false,
  // unmounting the autocomplete the user just engaged.
  // Map-assisted entry is the primary path; free-text is the escape hatch
  // for venues MapKit doesn't know about. Default ON so new entries land
  // straight in the autocomplete experience. The effect only forces ON
  // when coords hydrate (existing entity loading in) — it never forces
  // OFF, so a user who deliberately switched to manual entry stays there.
  const hasCoordinates = !!value?.coordinates;
  const [mapPinOn, setMapPinOn] = useState(true);
  useEffect(() => {
    if (hasCoordinates) setMapPinOn(true);
  }, [hasCoordinates]);

  const inputValue = value?.label ?? '';

  // ── MapKit autocomplete state ──
  const [mapkitReady, setMapkitReady] = useState(typeof mapkit !== 'undefined');
  const [mapkitError, setMapkitError] = useState<string | null>(null);
  const [mapKitOptions, setMapKitOptions] = useState<GroupedOption[]>([]);
  const [autocompleteLoading, setAutocompleteLoading] = useState(false);
  // Controlled open state lets the Verified/Custom button programmatically
  // open the dropdown to surface re-verification matches. Mirrors MUI's
  // default open-on-focus / open-on-input behavior via onOpen/onClose.
  const [autocompleteOpen, setAutocompleteOpen] = useState(false);
  const searchRef = useRef<mapkit.Search | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Monotonic query id so async Place resolutions can drop stale results
  // when the user types another character mid-flight. Each call to
  // triggerAutocompleteSearch increments this; resolution callbacks
  // capture the id at issue-time and bail if it no longer matches.
  const searchSeqRef = useRef(0);
  // Input ref so the Verified/Custom click can programmatically focus
  // the autocomplete input — controlling MUI's `open` prop alone isn't
  // enough; the popper renders but stays invisible without focus.
  const autocompleteInputRef = useRef<HTMLInputElement>(null);
  // Imperative handle on the mapkit.Map. mapkit-react's <Map> only
  // honors `initialRegion` once at mount — subsequent prop changes are
  // ignored. We use the ref to call setRegionAnimated() whenever the
  // picked coordinates or the bias region change, so the map follows
  // autocomplete picks instead of stranding the user on the empty-state
  // region they started from.
  //
  // State-backed callback ref (not useRef): refs aren't reactive, but
  // we need the pan-to-bias effect to re-fire when the Map mounts. Race
  // condition the old useRef pattern hit: editing a location that has
  // address parts but no coordinates → bias geocode lands faster than
  // the Map's first render → pan effect fires with mapRef.current still
  // null → no re-fire when the Map later mounts → user sees continental
  // US until they toggle Map Pin off and back on (which remounts and
  // recomputes initialRegion).
  const [mapInstance, setMapInstance] = useState<mapkit.Map | null>(null);
  // The <Map> mounts only while its Paper has a real box (see useNonZeroBox).
  const [mapBoxRef, mapBoxSized] = useNonZeroBox();
  // Mutable mirror of mapInstance, used only by the destroy path below.
  // mapkit-react@1.16.1's own unmount cleanup is dead code (stale
  // closure: its []-dep effect cleanup closes over the map state from
  // the first render, which is always null because the map is created
  // async after script load) — so every <Map> unmount leaks a live
  // WebGL context. Past Chrome's ~16/tab budget, the next map's
  // getContext() returns null and MapKit crashes inside its own
  // uncaught .then(). We destroy the instance ourselves on the
  // instance→null ref transition (pin toggle off / true unmount).
  const mapInstanceRef = useRef<mapkit.Map | null>(null);
  const mapRefCallback = useCallback((node: mapkit.Map | null) => {
    // MUST stay a block body returning void: in React 19, a callback
    // ref that RETURNS a function is treated as providing its own
    // cleanup and stops being called with null on detach — which would
    // silently kill this destroy path.
    const prev = mapInstanceRef.current;
    if (prev && prev !== node) {
      // Deferred one microtask: React detaches this (parent) ref BEFORE
      // running the child <Marker>'s unmount cleanup, and mapkit-react's
      // Marker calls map.removeAnnotation() in that cleanup. Destroying
      // synchronously here crashes MapKit inside the Marker cleanup
      // ("Cannot read properties of null (reading 'public')") and takes
      // down the route. Marker cleanups are synchronous layout-unmount
      // work inside the same commit, so a microtask runs strictly after
      // them.
      queueMicrotask(() => {
        try {
          prev.destroy();
        } catch {
          // A crashed/lost renderer must not throw.
        }
      });
    }
    mapInstanceRef.current = node;
    setMapInstance(node);
  }, []);

  // Unmount-only backstop. Normally a no-op: ref detach runs in the
  // commit mutation phase before passive-effect cleanup, and the
  // callback above nulls the mirror after destroying — so this cannot
  // double-destroy. It exists for any path where the ref detach is
  // skipped.
  useEffect(() => {
    return () => {
      const instance = mapInstanceRef.current;
      if (instance) {
        try {
          instance.destroy();
        } catch {
          // ignore — same rationale as above
        }
        mapInstanceRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    ensureMapKitJsLoaded()
      .then(() => {
        if (!cancelled) setMapkitReady(true);
      })
      .catch((err) => {
        if (!cancelled) {
          setMapkitError(
            err instanceof Error ? err.message : 'Failed to load map',
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!mapkitReady || typeof mapkit === 'undefined') return;
    if (!searchRef.current) searchRef.current = new mapkit.Search();
  }, [mapkitReady]);

  // WebGL preflight — second leg of the mapkit-react context-leak fix.
  // MapKit crashes with an unhandled rejection (bypassing the render
  // ErrorBoundary AND the script-load mapkitError fallback) when it
  // can't get a WebGL context. This must gate the RENDER, not merely
  // set an error from an effect: mapkit-react constructs the map in a
  // microtask once the loader promise is settled, so an error-state
  // re-render (a scheduler task) would land too late. Probing fresh on
  // every map mount cycle is cheap and doubles as a defense against
  // mid-session context exhaustion.
  const [webglChecked, setWebglChecked] = useState(false);
  useEffect(() => {
    if (!mapPinOn || !mapkitReady || mapkitError) return;
    if (!isWebGLAvailable()) {
      setMapkitError(
        "The interactive map can't start in this browser right now (no graphics context available). Venue search and the address fields still work — reload the page to retry the map.",
      );
      return;
    }
    setWebglChecked(true);
    return () => setWebglChecked(false);
  }, [mapPinOn, mapkitReady, mapkitError]);

  // ── Bias resolution ──
  // Picker-typed address parts win as bias text (most specific to what
  // the organizer is doing right now). Falls back to the parent-supplied
  // coord (e.g. event venue) when no typed parts exist yet.
  const internalBiasText = useMemo(() => {
    if (!value) return '';
    return [value.city, value.stateOrRegion, value.country]
      .filter(Boolean)
      .join(' ');
  }, [value]);

  const [resolvedBiasCoord, setResolvedBiasCoord] = useState<{
    lat: number;
    lng: number;
  } | null>(searchBiasCoordinate ?? null);

  useEffect(() => {
    if (!internalBiasText.trim()) {
      setResolvedBiasCoord(searchBiasCoordinate ?? null);
      return;
    }
    if (!mapkitReady || typeof mapkit === 'undefined') {
      setResolvedBiasCoord(searchBiasCoordinate ?? null);
      return;
    }
    const key = normalizeBiasText(internalBiasText);
    if (key in geocodeCache) {
      setResolvedBiasCoord(geocodeCache[key] ?? searchBiasCoordinate ?? null);
      return;
    }
    let cancelled = false;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const Geocoder = (mapkit as any).Geocoder;
    if (!Geocoder) return;
    const geocoder = new Geocoder({ getsUserLocation: false });
    geocoder.lookup(
      internalBiasText,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (_err: unknown, data: any) => {
        if (cancelled) return;
        const first = data?.results?.[0];
        const coord = first?.coordinate
          ? { lat: first.coordinate.latitude, lng: first.coordinate.longitude }
          : null;
        geocodeCache[key] = coord;
        setResolvedBiasCoord(coord ?? searchBiasCoordinate ?? null);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [searchBiasCoordinate, internalBiasText, mapkitReady]);

  // ── Existing-locations matches against the typed input ──
  // Excludes the record currently being edited (value.locationId) — picking
  // yourself from the dropdown is a no-op that just re-hydrates the same
  // doc, which reads as "the picker is blocking me" when an organizer
  // tries to upgrade an unverified record to verified.
  const existingMatches: GroupedOption[] = useMemo(() => {
    const q = inputValue.trim().toLowerCase();
    const editingId = value?.locationId ?? null;
    const sorted = existingLocations
      .slice()
      .filter((l) => l.id !== editingId)
      .sort((a, b) => a.label.localeCompare(b.label));
    const filtered = q
      ? sorted.filter((l) => l.label.toLowerCase().includes(q))
      : sorted;
    return filtered.slice(0, 10).map((l) => ({
      kind: 'existing' as const,
      id: l.id,
      label: l.label,
      sublabel: l.address ?? null,
      raw: l,
    }));
  }, [existingLocations, inputValue, value?.locationId]);

  const allOptions = useMemo(
    () => [...existingMatches, ...mapKitOptions],
    [existingMatches, mapKitOptions],
  );

  // ── Helpers ──

  /** Build a fresh "new" picker value with the given partial overrides. */
  const buildBlankValue = useCallback(
    (overrides: Partial<NonNullable<LocationPickerValue>> = {}): NonNullable<LocationPickerValue> => ({
      locationId: null,
      originalLocationId: null,
      verified: false,
      label: '',
      coordinates: null,
      address: null,
      city: null,
      stateOrRegion: null,
      country: null,
      color: initialColor,
      ...overrides,
    }),
    [initialColor],
  );

  /**
   * Apply an edit. If the picker is currently linked to a doc and the
   * edit is a manual one (not from a MapKit pick that re-verifies the
   * data), we set verified=false to mark drift. The locationId stays so
   * resolveLocationPickerValue updates the linked doc rather than
   * creating a duplicate.
   */
  const applyManualEdit = useCallback(
    (overrides: Partial<NonNullable<LocationPickerValue>>) => {
      if (value) {
        onChange({ ...value, ...overrides, verified: false });
      } else {
        onChange(buildBlankValue({ ...overrides, verified: false }));
      }
    },
    [value, onChange, buildBlankValue],
  );

  // ── Handlers ──

  const handleTogglePin = useCallback(() => {
    setMapPinOn((prev) => {
      const next = !prev;
      if (!next && value?.coordinates) {
        // Toggling off: drop the coords. Keep address/label so the user
        // doesn't lose their work.
        onChange({ ...value, coordinates: null, verified: false });
      }
      return next;
    });
  }, [value, onChange]);

  /**
   * Trigger the MapKit autocomplete search for the given query and
   * populate `mapKitOptions`. Extracted from handleInputChange so the
   * Verified/Custom button click can also use it (re-verify flow:
   * open the dropdown with the user's current data already searched).
   *
   * Two-phase result population. Apple's `SearchAutocompleteResult` is
   * intentionally sparse — `displayLines` is a presentation array Apple
   * fills as they see fit (sometimes [name, address], sometimes just
   * [name]). The full address only lives on `Place`, returned by
   * `Search.search(autocompleteResult)`. We use both:
   *
   *   Phase 1 (fast): set initial options from autocomplete results so
   *   the dropdown opens immediately with the names. sublabel is
   *   whatever displayLines gave us — may be empty.
   *
   *   Phase 2 (enriching): for each result, resolve to a Place via
   *   search() and update its sublabel with `place.formattedAddress`,
   *   which is required on Place and country-formatted by Apple. Each
   *   row's sublabel populates as resolution returns; results that race
   *   ahead of a newer query are dropped via the searchSeq token.
   */
  const triggerAutocompleteSearch = useCallback(
    (query: string) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (!query.trim() || !searchRef.current) {
        setMapKitOptions([]);
        return;
      }
      const queryId = ++searchSeqRef.current;
      setAutocompleteLoading(true);
      debounceRef.current = setTimeout(() => {
        const cb: mapkit.AutocompleteSearchCallback = (error, data) => {
          // Drop stale resolutions when the user has typed again.
          if (queryId !== searchSeqRef.current) return;
          setAutocompleteLoading(false);
          if (error || !data?.results) {
            setMapKitOptions([]);
            return;
          }
          const initialOptions: GroupedOption[] = data.results
            .slice(0, 8)
            .map((result) => ({
              kind: 'mapkit' as const,
              label: result.displayLines?.[0] ?? query,
              sublabel: result.displayLines?.[1] ?? '',
              raw: result,
            }));
          setMapKitOptions(initialOptions);

          // Phase 2: resolve each suggestion to a full Place and update
          // its sublabel with formattedAddress. Updates land out of
          // order; the per-row matcher (raw === option.raw) makes the
          // update target the right row even if the array shifts.
          initialOptions.forEach((option) => {
            if (option.kind !== 'mapkit') return;
            if (!searchRef.current) return;
            searchRef.current.search(
              option.raw,
              ((
                err: Error | null,
                placeData: { places: mapkit.Place[] } | null,
              ) => {
                if (queryId !== searchSeqRef.current) return;
                if (err || !placeData?.places?.[0]) return;
                const place = placeData.places[0];
                const richAddress = place.formattedAddress;
                if (!richAddress) return;
                setMapKitOptions((prev) =>
                  prev.map((opt) =>
                    opt.kind === 'mapkit' && opt.raw === option.raw
                      ? { ...opt, sublabel: richAddress }
                      : opt,
                  ),
                );
              }) as mapkit.SearchDelegate,
            );
          });
        };
        const opts: mapkit.SearchAutocompleteOptions = {
          includeQueries: false,
          includeAddresses: true,
        };
        if (resolvedBiasCoord) {
          opts.coordinate = new mapkit.Coordinate(
            resolvedBiasCoord.lat,
            resolvedBiasCoord.lng,
          );
          opts.region = new mapkit.CoordinateRegion(
            new mapkit.Coordinate(
              resolvedBiasCoord.lat,
              resolvedBiasCoord.lng,
            ),
            new mapkit.CoordinateSpan(1.0, 1.0),
          );
        }
        searchRef.current!.autocomplete(query, cb, opts);
      }, 300);
    },
    [resolvedBiasCoord],
  );

  // On hydration of an unverified existing record (locationId set, no
  // coords yet, label populated), proactively fire MapKit autocomplete
  // with the label so the dropdown's "Or pick from map" group is
  // pre-populated. Without this, an organizer opening a label-only
  // location has to type a character to surface Apple Maps options,
  // which reads as "the picker is blocking me from upgrading this
  // record." Re-fires on label or bias change; coordinates landing
  // turns it off via the precondition guard so verified picks don't
  // re-trigger searches.
  useEffect(() => {
    if (!mapkitReady) return;
    if (!value?.locationId) return;
    if (value.coordinates) return;
    if (!value.label) return;
    triggerAutocompleteSearch(value.label);
  }, [
    mapkitReady,
    value?.locationId,
    value?.label,
    value?.coordinates,
    triggerAutocompleteSearch,
  ]);

  /** Free-text edits to the label input (toggle off and on share this). */
  const handleInputChange = useCallback(
    (_event: React.SyntheticEvent | null, newInputValue: string) => {
      if (mapPinOn) {
        triggerAutocompleteSearch(newInputValue);
      }
      // Update the picker value. Empty label preserves typed address
      // parts (clearing the venue search shouldn't wipe city/state).
      // resolveLocationPickerValue treats fully-empty as null at submit.
      applyManualEdit({ label: newInputValue });
    },
    [mapPinOn, triggerAutocompleteSearch, applyManualEdit],
  );

  const handleAutocompleteInputFocus = useCallback(() => {
    setAutocompleteOpen(true);
    const q = inputValue.trim();
    if (q) triggerAutocompleteSearch(q);
  }, [inputValue, triggerAutocompleteSearch]);

  /**
   * Selection from the autocomplete dropdown.
   *   - Existing → adopt the saved doc as the picker value (locationId
   *     set, verified inherited from the saved flag).
   *   - MapKit → resolve to a full Place via search() to get structured
   *     fields, then load into the picker value as Verified.
   */
  const handleSelect = useCallback(
    (
      _event: React.SyntheticEvent | null,
      option: GroupedOption | string | null,
    ) => {
      if (!option || typeof option === 'string') return;
      if (option.kind === 'existing') {
        onChange(pickerValueFromLocation(option.raw));
        setMapKitOptions([]);
        return;
      }
      // MapKit option: resolve to a full Place.
      if (!searchRef.current) return;
      searchRef.current.search(
        option.raw,
        ((error: Error | null, data: { places: mapkit.Place[] } | null) => {
          if (error || !data?.places?.[0]) {
            // Fallback: use the autocomplete's coordinate. Verified=true
            // because the user picked from MapKit even though structured
            // fields aren't available. originalLocationId stays as
            // whatever was hydrated — MapKit picks don't change which
            // doc we're "editing", they just refresh the data we'd
            // write to it.
            if (option.raw.coordinate) {
              onChange({
                locationId: value?.locationId ?? null,
                originalLocationId: value?.originalLocationId ?? null,
                verified: true,
                label: option.label,
                coordinates: {
                  lat: option.raw.coordinate.latitude,
                  lng: option.raw.coordinate.longitude,
                },
                address: option.sublabel || null,
                city: null,
                stateOrRegion: null,
                country: null,
                color: value?.color ?? initialColor,
              });
              setMapKitOptions([]);
            }
            return;
          }
          const place = data.places[0];
          onChange({
            locationId: value?.locationId ?? null,
            originalLocationId: value?.originalLocationId ?? null,
            verified: true,
            label: place.name ?? option.label,
            coordinates: {
              lat: place.coordinate.latitude,
              lng: place.coordinate.longitude,
            },
            address: place.fullThoroughfare ?? null,
            city: place.locality ?? null,
            stateOrRegion: place.administrativeArea ?? null,
            country: place.country ?? null,
            color: value?.color ?? initialColor,
          });
          setMapKitOptions([]);
        }) as mapkit.SearchDelegate,
      );
    },
    [onChange, value, initialColor],
  );

  /** Map tap drops a manual pin. Custom (verified=false) by definition. */
  const handleMapTap = useCallback(
    (event: MapInteractionEvent) => {
      const coord = event.toCoordinates();
      applyManualEdit({
        coordinates: { lat: coord.latitude, lng: coord.longitude },
        label: value?.label || 'New location',
      });
    },
    [value, applyManualEdit],
  );

  const handleColorChange = useCallback(
    (nextColor: string) => {
      // Color override doesn't break verification — color is metadata
      // about the pin, not about the address data Apple validated.
      if (!value) {
        onChange(buildBlankValue({ color: nextColor }));
        return;
      }
      onChange({ ...value, color: nextColor });
    },
    [value, onChange, buildBlankValue],
  );

  const handleClear = useCallback(() => {
    onChange(null);
    setMapKitOptions([]);
  }, [onChange]);

  /**
   * Editing/New toggle — the picker's central save-mode control. When
   * the picker is hydrated from a saved doc (`originalLocationId` set),
   * the organizer can choose between updating that doc on save
   * ("Editing") or creating a new doc and leaving the original alone
   * ("New copy"). Toggling between them just swaps `locationId`
   * between `originalLocationId` and null; field values stay intact in
   * either direction.
   *
   * No-op when there's nothing to fork from (originalLocationId null).
   */
  const handleEditingNewToggle = useCallback(() => {
    if (!value?.originalLocationId) return;
    if (value.locationId) {
      onChange({ ...value, locationId: null });
    } else {
      onChange({ ...value, locationId: value.originalLocationId });
    }
  }, [value, onChange]);

  /**
   * Verified/Custom button click. The status itself updates
   * automatically: any MapKit pick → Verified, any manual edit →
   * Custom. The button click is the *re-verify* affordance — when
   * Custom, it opens the venue search so the organizer can pick a
   * verified match against their current data.
   */
  const handleVerifiedCustomClick = useCallback(() => {
    if (!value) return;
    if (value.verified) {
      // Already verified — no destructive action. Tooltip carries
      // the explanation; nothing to do here.
      return;
    }
    // Custom → open the search workflow so the organizer can re-verify
    // by picking a MapKit match. Turning pin on shows the autocomplete
    // input + map preview; the deferred focus + open lets the popper
    // actually render (MUI's controlled `open` requires focus to
    // surface the dropdown on a freshly-mounted Autocomplete).
    setMapPinOn(true);
    triggerAutocompleteSearch(value.label);
    // Defer to the next tick so the Autocomplete is mounted (when
    // mapPinOn flips, the picker swaps TextField → Autocomplete).
    setTimeout(() => {
      autocompleteInputRef.current?.focus();
      setAutocompleteOpen(true);
    }, 0);
  }, [value, triggerAutocompleteSearch]);

  // ── Field-edit handlers (Address Details) ──

  const handleAddressFieldChange = useCallback(
    (
      field: 'address' | 'city' | 'stateOrRegion' | 'country',
      next: string | null,
    ) => {
      applyManualEdit({ [field]: next });
    },
    [applyManualEdit],
  );

  /**
   * MapKit pick on a sub-field (City / State / Country). Cascade-fills
   * the neighbors with the resolved Place's structured data, and re-
   * verifies the data. The field that was picked uses the dropdown's
   * displayed text (split on first comma) so picking "Jackson Hole, WY"
   * lands "Jackson Hole" in the City field rather than Apple's
   * incorporated municipality name "Jackson".
   */
  const handleSubfieldPick = useCallback(
    (
      field: 'city' | 'stateOrRegion' | 'country',
      pickedText: string,
      parts: ResolvedPlaceParts,
    ) => {
      const base = value ?? buildBlankValue();
      const next = { ...base, verified: true };
      // The picked field uses the dropdown text (what the user saw).
      next[field] = pickedText;
      // Cascade-fill neighbors only if currently empty so we don't
      // overwrite values the organizer typed.
      if (field === 'city') {
        if (!base.stateOrRegion && parts.administrativeArea)
          next.stateOrRegion = parts.administrativeArea;
        if (!base.country && parts.country) next.country = parts.country;
      } else if (field === 'stateOrRegion') {
        if (!base.country && parts.country) next.country = parts.country;
      }
      onChange(next);
    },
    [value, onChange, buildBlankValue],
  );

  // ── Derived display state ──

  const currentCoordinates = value?.coordinates ?? null;
  const currentColor = value?.color ?? initialColor;

  // ── Map region control ──
  // mapkit-react's <Map> only honors `initialRegion` once at mount, so we
  // imperatively update the underlying mapkit.Map via setRegionAnimated()
  // for any region change that happens post-mount (autocomplete pick,
  // typed-address geocode, etc.). Two regions are tracked separately:
  //
  //   1. A picked location (currentCoordinates) → tight zoom (delta 0.005, ~500m)
  //   2. A bias hint with no pick yet (resolvedBiasCoord, e.g. event
  //      venue passed from a Session/Group/Travel form) → wider regional
  //      view (delta 0.05) so the organizer can see the surrounding area
  //      before placing a pin.
  //
  // The picked-location effect wins over the bias effect — they're
  // mutually exclusive in the UI, but ordering still matters for the
  // first paint after a pick clears.

  const initialMapRegion = useMemo(() => {
    if (currentCoordinates) {
      return {
        centerLatitude: currentCoordinates.lat,
        centerLongitude: currentCoordinates.lng,
        latitudeDelta: 0.005,
        longitudeDelta: 0.005,
      };
    }
    if (searchBiasCoordinate) {
      return {
        centerLatitude: searchBiasCoordinate.lat,
        centerLongitude: searchBiasCoordinate.lng,
        latitudeDelta: 0.05,
        longitudeDelta: 0.05,
      };
    }
    if (resolvedBiasCoord) {
      return {
        centerLatitude: resolvedBiasCoord.lat,
        centerLongitude: resolvedBiasCoord.lng,
        latitudeDelta: 0.5,
        longitudeDelta: 0.5,
      };
    }
    // Last-resort: continental US. Only hit when the picker has no
    // parent-supplied bias AND the user hasn't typed any address parts
    // yet (typical only on EventForm before any geocoding lands).
    return {
      centerLatitude: 39.8,
      centerLongitude: -98.5,
      latitudeDelta: 40,
      longitudeDelta: 60,
    };
    // Recomputed every render so the value at the moment Map actually
    // mounts is fresh. mapkit-react ignores initialRegion changes for
    // an already-mounted Map (we update via setRegionAnimated below),
    // so re-renders are cheap. The first-mount case matters: Map only
    // mounts when mapPinOn flips true, which can be well after the
    // picker hydrates from a saved locationId — by that point coords
    // are populated and we want Map to land tight on them, not on the
    // continental-US default.
  }, [
    currentCoordinates?.lat,
    currentCoordinates?.lng,
    searchBiasCoordinate?.lat,
    searchBiasCoordinate?.lng,
    resolvedBiasCoord?.lat,
    resolvedBiasCoord?.lng,
  ]);

  // Pan/zoom to picked coordinates whenever they change post-mount.
  // mapInstance is in the dep array (not just read inside) so the effect
  // re-fires when the Map first mounts — fixes the race where the pick
  // is set before the Map has rendered.
  useEffect(() => {
    if (!mapInstance || typeof mapkit === 'undefined') return;
    if (!currentCoordinates) return;
    const region = new mapkit.CoordinateRegion(
      new mapkit.Coordinate(currentCoordinates.lat, currentCoordinates.lng),
      new mapkit.CoordinateSpan(0.005, 0.005),
    );
    mapInstance.setRegionAnimated(region, true);
  }, [mapInstance, currentCoordinates?.lat, currentCoordinates?.lng]);

  // Pan to bias region when there's no picked coordinate yet but a
  // typed-address geocode (or parent-supplied venue) lands. Lets the
  // organizer see the right area before placing a pin. mapInstance is
  // in the dep array so this re-fires on first Map mount even if the
  // bias geocode resolved earlier (otherwise the user sees continental
  // US until they toggle Map Pin off and on).
  useEffect(() => {
    if (!mapInstance || typeof mapkit === 'undefined') return;
    if (currentCoordinates) return;
    const bias = searchBiasCoordinate ?? resolvedBiasCoord;
    if (!bias) return;
    const region = new mapkit.CoordinateRegion(
      new mapkit.Coordinate(bias.lat, bias.lng),
      new mapkit.CoordinateSpan(0.05, 0.05),
    );
    mapInstance.setRegionAnimated(region, true);
  }, [
    mapInstance,
    currentCoordinates,
    searchBiasCoordinate?.lat,
    searchBiasCoordinate?.lng,
    resolvedBiasCoord?.lat,
    resolvedBiasCoord?.lng,
  ]);

  // Show a dimmer event-venue reference pin on Session/Group/Travel/POI
  // pickers (anywhere a parent passed searchBiasCoordinate). Suppressed
  // when the picker IS the event venue picker (no parent coord) or when
  // the picked coord coincides with the venue (~10m precision).
  const showVenueReferencePin = useMemo(() => {
    if (!searchBiasCoordinate) return false;
    if (!currentCoordinates) return true;
    const epsilon = 0.0001; // ~11m at the equator
    const dLat = Math.abs(searchBiasCoordinate.lat - currentCoordinates.lat);
    const dLng = Math.abs(searchBiasCoordinate.lng - currentCoordinates.lng);
    return dLat > epsilon || dLng > epsilon;
  }, [searchBiasCoordinate, currentCoordinates]);
  const isVerified = !!value?.verified;
  const isEditing = !!value?.locationId; // Save will update an existing doc
  const canFork = !!value?.originalLocationId; // We have something to fork from
  const showAddressFields = !!value || mapPinOn;

  return (
    <Box data-testid={testId('location-picker')}>
      {/* Phone: search on its own full-width row, toggles wrap below it. */}
      <Box
        sx={
          isPhone
            ? { display: 'flex', flexDirection: 'column', gap: 1 }
            : { display: 'flex', gap: 1, alignItems: 'flex-start' }
        }
      >
        <Box sx={{ flex: 1 }}>
          {mapPinOn ? (
            <MuiAutocomplete<GroupedOption, false, false, true>
              freeSolo
              open={autocompleteOpen}
              onOpen={() => setAutocompleteOpen(true)}
              onClose={() => setAutocompleteOpen(false)}
              options={allOptions}
              loading={autocompleteLoading}
              inputValue={inputValue}
              onInputChange={handleInputChange}
              onChange={handleSelect}
              getOptionLabel={(option) =>
                typeof option === 'string' ? option : option.label
              }
              groupBy={(option) =>
                option.kind === 'existing'
                  ? 'Existing locations'
                  : 'Or pick from map'
              }
              isOptionEqualToValue={(a, b) =>
                a.kind === b.kind &&
                a.label === b.label &&
                (a.kind === 'existing'
                  ? a.id ===
                    (b as Extract<GroupedOption, { kind: 'existing' }>).id
                  : true)
              }
              filterOptions={(x) => x}
              renderOption={({ key, ...props }, option) => (
                <li key={key} {...props}>
                  <Box>
                    <Typography variant="body2">{option.label}</Typography>
                    {option.sublabel && (
                      <Typography variant="caption" color="text.secondary">
                        {option.sublabel}
                      </Typography>
                    )}
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  placeholder="Search venues or pick existing"
                  onFocus={handleAutocompleteInputFocus}
                  onClick={handleAutocompleteInputFocus}
                  inputRef={autocompleteInputRef}
                  inputProps={{
                    ...params.inputProps,
                    'data-testid': testId('location-picker-input'),
                  }}
                  slotProps={{
                    input: {
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {autocompleteLoading ? (
                            <CircularProgress size={16} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    },
                  }}
                />
              )}
            />
          ) : (
            <TextField
              size="small"
              fullWidth
              placeholder="Location label"
              value={inputValue}
              onChange={(e) => handleInputChange(null, e.target.value)}
              inputProps={{ 'data-testid': testId('location-picker-input') }}
              slotProps={{
                input: {
                  endAdornment: inputValue ? (
                    <IconButton
                      size="small"
                      onClick={handleClear}
                      title="Clear"
                      data-testid={testId('location-picker-clear')}
                    >
                      <X size={14} />
                    </IconButton>
                  ) : null,
                },
              }}
            />
          )}
        </Box>
        {/* Three-button row of save/provenance/pin controls. Each
            communicates its current state via label + tooltip; the
            click action is described in the tooltip too so organizers
            understand what changes when they tap. */}
        <Box
          sx={
            isPhone
              ? { display: 'flex', gap: 0.5, flexWrap: 'wrap' }
              : { display: 'flex', gap: 0.5, flexShrink: 0 }
          }
        >
          {canFork && (
            <Tooltip
              title={
                isEditing
                  ? 'Editing — save will update the original location (affects everywhere it is used). Click to switch to New copy.'
                  : 'New copy — save will create a new location, leaving the original alone. Click to switch back to Editing.'
              }
            >
              <ToggleButton
                value="editing-new"
                selected={isEditing}
                onChange={handleEditingNewToggle}
                size="small"
                sx={{ height: 40, px: 1.5 }}
                data-testid={testId('location-picker-editing-toggle')}
              >
                {isEditing ? (
                  <Pencil size={14} style={{ marginRight: 4 }} />
                ) : (
                  <Plus size={14} style={{ marginRight: 4 }} />
                )}
                {isEditing ? 'Editing' : 'New copy'}
              </ToggleButton>
            </Tooltip>
          )}

          {value && (
            <Tooltip
              title={
                isVerified
                  ? 'Address data verified against Apple Maps. Editing any field will mark this Custom.'
                  : 'Hand-authored — not matched against Apple Maps. Click to search Apple Maps for a verified match.'
              }
            >
              <ToggleButton
                value="verified"
                selected={isVerified}
                onChange={handleVerifiedCustomClick}
                size="small"
                sx={{ height: 40, px: 1.5 }}
                data-testid={testId('location-picker-verified-toggle')}
              >
                {isVerified ? (
                  <Check size={14} style={{ marginRight: 4 }} />
                ) : (
                  <Pencil size={14} style={{ marginRight: 4 }} />
                )}
                {isVerified ? 'Verified' : 'Custom'}
              </ToggleButton>
            </Tooltip>
          )}

          <Tooltip
            title={
              mapPinOn
                ? 'Map pin on — search a venue or click the map. Click to drop the pin.'
                : 'Label-only — no map pin. Click to add a pin.'
            }
          >
            <ToggleButton
              value="map-pin"
              selected={mapPinOn}
              onChange={handleTogglePin}
              size="small"
              sx={{ height: 40, px: 1.5 }}
              data-testid={testId('location-picker-toggle')}
            >
              <MapPin size={14} style={{ marginRight: 4 }} />
              Map Pin
            </ToggleButton>
          </Tooltip>
        </Box>
      </Box>

      {(mapPinOn || showAddressFields) && (
        <Grid
          container
          spacing={layout === 'split' ? 2 : 0}
          sx={{ mt: layout === 'split' ? 1 : 0 }}
        >
          {mapPinOn && (
            <Grid size={{ xs: 12, md: layout === 'split' ? 7 : 12 }}>
              {mapkitError ? (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    mt: layout === 'split' ? 0 : 1,
                    textAlign: 'center',
                    color: 'text.secondary',
                  }}
                >
                  <Typography variant="body2">
                    Map unavailable: {mapkitError}
                  </Typography>
                </Paper>
              ) : !mapkitReady || !webglChecked ? (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    mt: layout === 'split' ? 0 : 1,
                    textAlign: 'center',
                    color: 'text.secondary',
                  }}
                >
                  <Typography variant="body2">Loading map…</Typography>
                </Paper>
              ) : (
                <>
                  <Paper
                    ref={mapBoxRef}
                    variant="outlined"
                    sx={{
                      // Split mode: stretch to match the form column's
                      // natural height (address fields + pin color
                      // accordion is taller than the default 320). Stacked
                      // mode: keep the explicit mapHeight.
                      height: layout === 'split' ? '100%' : mapHeight,
                      minHeight: mapHeight,
                      mt: layout === 'split' ? 0 : 1,
                      overflow: 'hidden',
                      borderRadius: 1,
                      position: 'relative',
                    }}
                  >
                    {mapBoxSized ? (
                      <MapErrorBoundary
                        fallback={(retry) => (
                          <Box
                            data-testid={testId('location-picker-map-failed')}
                            sx={{
                              p: 2,
                              textAlign: 'center',
                              color: 'text.secondary',
                            }}
                          >
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              Map unavailable: the map failed to start.
                              Venue search and the address fields still
                              work.
                            </Typography>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={retry}
                              data-testid={testId('location-picker-map-retry')}
                            >
                              Retry map
                            </Button>
                          </Box>
                        )}
                      >
                        <Map
                          ref={mapRefCallback}
                          token=""
                          load={async () => {
                            await ensureMapKitJsLoaded();
                          }}
                          showsMapTypeControl={false}
                          isScrollEnabled={!isPhone || mapUnlocked}
                          isZoomEnabled={!isPhone || mapUnlocked}
                          initialRegion={initialMapRegion}
                          onSingleTap={handleMapTap}
                        >
                          {showVenueReferencePin && searchBiasCoordinate && (
                            <Marker
                              latitude={searchBiasCoordinate.lat}
                              longitude={searchBiasCoordinate.lng}
                              color="#9CA3AF"
                              title="Event venue"
                              subtitle="(reference)"
                            />
                          )}
                          {currentCoordinates && (
                            <Marker
                              latitude={currentCoordinates.lat}
                              longitude={currentCoordinates.lng}
                              color={currentColor}
                            />
                          )}
                        </Map>
                      </MapErrorBoundary>
                    ) : (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ p: 2, textAlign: 'center' }}
                      >
                        Loading map…
                      </Typography>
                    )}
                    {/* Phone: the map starts locked under a cover so a
                        finger dragging over it scrolls the page. One tap
                        unlocks it; Done locks it again. */}
                    {isPhone && !mapUnlocked && (
                      <Box
                        role="button"
                        tabIndex={0}
                        aria-label="Unlock the map to move it or drop a pin"
                        onClick={() => setMapUnlocked(true)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') setMapUnlocked(true);
                        }}
                        data-testid={testId('location-picker-map-unlock')}
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          zIndex: 2,
                          display: 'flex',
                          alignItems: 'flex-end',
                          justifyContent: 'center',
                          pb: 1.5,
                          cursor: 'pointer',
                        }}
                      >
                        <Chip
                          label="Tap to move the map"
                          size="small"
                          sx={{ bgcolor: 'background.paper', boxShadow: 1, pointerEvents: 'none' }}
                        />
                      </Box>
                    )}
                    {isPhone && mapUnlocked && (
                      <Chip
                        label="Done"
                        color="primary"
                        onClick={() => setMapUnlocked(false)}
                        data-testid={testId('location-picker-map-lock')}
                        sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2 }}
                      />
                    )}
                  </Paper>

                  {currentCoordinates && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', mt: 0.5 }}
                      data-testid={testId('location-picker-coordinates')}
                    >
                      {currentCoordinates.lat.toFixed(4)},{' '}
                      {currentCoordinates.lng.toFixed(4)}
                    </Typography>
                  )}
                </>
              )}
            </Grid>
          )}

          {(showAddressFields || mapPinOn) && (
            <Grid size={{ xs: 12, md: layout === 'split' ? 5 : 12 }}>
              {showAddressFields && (
                <Box
                  sx={{
                    // In stacked mode, gap from the map block above. In
                    // split mode, no gap (this column is a sibling of the
                    // map column, not below it).
                    mt: layout === 'split' ? 0 : 1.5,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.25,
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    Address details
                  </Typography>
                  <TextField
                    size="small"
                    label="Street"
                    value={value?.address ?? ''}
                    onChange={(e) =>
                      handleAddressFieldChange('address', e.target.value || null)
                    }
                    helperText="Auto-fills from the venue pick. Editable."
                    inputProps={{
                      'data-testid': testId('location-picker-address'),
                    }}
                  />
                  <MapKitAutocompleteField
                    label="City"
                    value={value?.city ?? ''}
                    onChange={(next) => handleAddressFieldChange('city', next || null)}
                    onPick={(pickedText, parts) =>
                      handleSubfieldPick('city', pickedText, parts)
                    }
                    category="Locality"
                    biasCoordinate={resolvedBiasCoord ?? undefined}
                    testIdSuffix={`${testIdSuffix ?? 'picker'}-city`}
                  />
                  <MapKitAutocompleteField
                    label="State / Region"
                    value={value?.stateOrRegion ?? ''}
                    onChange={(next) =>
                      handleAddressFieldChange('stateOrRegion', next || null)
                    }
                    onPick={(pickedText, parts) =>
                      handleSubfieldPick('stateOrRegion', pickedText, parts)
                    }
                    category="AdministrativeArea"
                    biasCoordinate={resolvedBiasCoord ?? undefined}
                    testIdSuffix={`${testIdSuffix ?? 'picker'}-state`}
                  />
                  <MapKitAutocompleteField
                    label="Country"
                    value={value?.country ?? ''}
                    onChange={(next) =>
                      handleAddressFieldChange('country', next || null)
                    }
                    onPick={(pickedText, parts) =>
                      handleSubfieldPick('country', pickedText, parts)
                    }
                    category="Country"
                    testIdSuffix={`${testIdSuffix ?? 'picker'}-country`}
                  />
                </Box>
              )}

              {mapPinOn && (
                <Accordion
                  disableGutters
                  elevation={0}
                  sx={{
                    mt: 1,
                    '&:before': { display: 'none' },
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                  }}
                >
          <AccordionSummary
            expandIcon={<ChevronDown size={18} />}
            sx={{ minHeight: 40, '& .MuiAccordionSummary-content': { my: 0 } }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 14,
                  height: 14,
                  borderRadius: '50%',
                  backgroundColor: currentColor,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              />
              <Typography variant="body2">Pin color</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <MuiColorInput
              format="hex"
              value={currentColor}
              onChange={handleColorChange}
              isAlphaHidden
              size="small"
            />
          </AccordionDetails>
        </Accordion>
      )}
            </Grid>
          )}
        </Grid>
      )}
    </Box>
  );
}
